export const MODE_HELPER_TEXT =
  'When enabled, the block will wait until the conversation is closed before moving forward';

export const TIMEOUT_HELPER_TEXT =
  'Maximum duration the entire conversation can remain active, regardless of message activity.';

export const PATIENT_REPLY_TIMEOUT_HELPER_TEXT =
  "How long to wait for the patient to reply after each message is sent. If the patient doesn't reply within this time, the conversation will close.";

export const ConversationBlockDocumentationMarkdown = `
> ⚠️ **HIPAA Security Notice**
> 
> SMS communications are not encrypted or secure. DO NOT configure workflows that request, collect, or transmit PHI via SMS.
>
> PHI includes: patient names, medical conditions, symptoms, medications, appointment details with providers, or any health-related information.

# Manage SMS Conversation

This block lets you manage the full lifecycle of an SMS conversation by creating, reopening, replying to, or closing a conversation. It passes patient context and the message to our conversations service, which opens a conversation window where subsequent patient replies and staff/assistant responses are threaded and tracked until the conversation is closed or times out. You can control whether the conversation remains open or closed while optionally sending a message as part of each action.

## Configuration

- **Mode**: Choose between Legacy mode (deprecated ), Open, Reopen, Reply,Or  Close mode. 
  - **Create**: Allow you to create a new conversation and wait until the conversation is closed.
  - **Reopen**: Allow you to reopen a closed conversation and wait until the conversation is closed. If the conversation is not closed already, the block will just send the configured message.
  - **Reply**: Allow you to reply to a conversation. You can reply to closed conversations and the conversation itself will stay closed.
  - **Close**: Allow you to close the conversation and additionally send a message when closing. If the conversation is already closed, the block will do nothing and just moves forward.
  - **Legacy**: Allow you to create/reopen/reply to a conversation, see configuration below. This is now replaced with Create/Reopen/Reply block mode.

### Legacy Mode Configuration

- **Existing Conversation ID**: Provide an existing conversation ID to continue an ongoing conversation. When enabled, you will only be able to provide the message and a new timeout.
- **Agent Phone Number**: The phone number to send the message from.
- **First Message**: The first message to send to the patient when starting the conversation.
- **Tags**: Metadata labels that can be associated with the conversation. Tags must be primitive string values rather than objects. Tags that are null, undefined, or empty strings will be ignored.
- **Priority**: The priority of the conversation.
- **Wait for conversation to close**: When enabled, the block will wait until the conversation is closed before moving forward. When disabled, the flow will continue immediately after starting the conversation. Default is waiting until the conversation is closed.
- **Timeout**: The time to wait before considering the conversation timed out and moving forward.
- **Patient Reply Handler Flow**: When enabled, allows you to specify a flow that will be triggered when the patient replies to the conversation.
  - **Flow**: The flow to be triggered when the patient replies.
  - **Version Target**: The strategy for selecting which version of the flow to use.
  - **Flow Version**: When using "Specific Version" target, select the specific version of the flow to use.

### Create Mode Configuration
- **Agent Phone Number**: The phone number to send the message from.
- **Message**: The message to send to the patient when starting the conversation.
- **Tags**: Metadata labels that can be associated with the conversation. Tags must be primitive string values rather than objects. Tags that are null, undefined, or empty strings will be ignored.
- **Priority**: The priority of the conversation.
- **Timeout**: The time to wait before considering the conversation timed out and moving forward without being closed.

### Reopen Mode Configuration
- **Existing Conversation ID**: The ID of the conversation to reopen.
- **Message**: The message to send to the patient when reopening the conversation. It is optional, if not provided, the conversation will be reopened without sending a message.
- **Timeout**: The time to wait before considering the conversation timed out and moving forward without being closed.

### Close Mode Configuration
- **Existing Conversation ID**: The ID of the conversation to close.
- **Message**: The message to send to the patient when closing the conversation. It is optional, if not provided, the conversation will be closed without sending a message.
- **Resolution Reason**: The reason for closing the conversation.

### Reply Mode Configuration
- **Existing Conversation ID**: The ID of the conversation to reply to.
- **Message**: The message to send to the patient when replying to the conversation.


## Note

### Phone Number Selection 
- The system automatically selects the best phone number for each patient using a priority-based approach: Cell (1st priority) → Home (2nd priority) → Work (3rd priority). 
- If a patient has a preferred number set, it takes priority. The system also considers the last successfully delivered number for subsequent messages. 
- Invalid numbers (null, empty, unsubscribed, or not allowed number type per organization configuration - for example work number maybe configured to not be used for texting) are excluded from selection.
`;
